/* eslint-disable react-hooks/exhaustive-deps */
import { capitalize, intersectionWith, isEqual } from "lodash";
import { find, isNil } from "lodash/fp";
import router from "next/router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import {
  CartCalculationModel,
  ConsultationSessionType,
  EvalIds,
  ProductNameType,
  RegionType,
  StripeProductModel,
  useDeleteCartItemByIdMutation,
  useGetCartQuery,
  useInsertCartItemByIdMutation,
  useUpdateCartItemByIdMutation
} from "graphql/generated";
import graphQLClient from "graphql/react-query";
import { ENABLE_NEW_PRESCRIPTION_FLOW } from "src/config";
import {
  URL_PATIENT_EVAL_V2,
  URL_PRESCRIPTION_FLOW_WITH_EVAL_ID_V2
} from "src/constants/url.constants";
import {
  activeProductsState,
  cartState,
  isAddingCartState,
  isBuyNowState,
  isCancelModalRemovingState,
  isCartUpdatingState,
  isConfictPlatform,
  isDetailModalOpenedState,
  isPriceModalOpenedState,
  isSameEvalModalOpenedState,
  modalCancelingItemState,
  orderCalculationState,
  patientState,
  pendingEvalIdsState,
  platformState,
  priceModalSelectedPriceState,
  priceModalSelectedProductState,
  priceModalShowPriceStep,
  promoCodeState,
  sameEvalIdState,
  selectedConsultTypeState,
  suggestionItemsState
} from "src/states";
import { convertStringToEnumKey } from "src/utils";
import { checkCountry } from "src/utils/checkCountry";
import { getTracking, getUtm } from "src/utils/tracking";

import { useFetchExcludedEvalIds } from "../../Dashboard/hooks/useFetchExcludedEvalIds";

export const GynaeGroupProducts: string[] = [
  ProductNameType.FertilityHormoneTestKit,
  ProductNameType.GynaeConsultHormoneTest,
  ProductNameType.GynaeConsult
];

// return true if there's exist one product in group GynaeGroupProducts and in productNames
export const checkOrderGynaeHormoneTest = (
  product: StripeProductModel,
  productNames: string[]
): boolean => {
  return (
    GynaeGroupProducts.includes(product.uniqName) &&
    intersectionWith(productNames, GynaeGroupProducts, isEqual).length !== 0
  );
};

export type AddToCartClickPayload = {
  productId: string;
  priceId: string;
  completedEval?: {
    id: string;
    evalId: string;
    consultType?: ConsultationSessionType;
  };
  teleConsultForSkipEval?: string;
  isSkipMedSelecting?: boolean;
  patientNote?: string;
  autoRenewal?: boolean;
};

const useCartHooks = (isSuggestion = false, initExcludeEvalIds?: string[]) => {
  const queryClient = useQueryClient();
  const patient = useRecoilValue(patientState);
  const { mutateAsync: updateCartItemById } = useUpdateCartItemByIdMutation(graphQLClient);
  const { mutateAsync: insertCartItemById, mutateAsync: insertCartItemByIdAsync } =
    useInsertCartItemByIdMutation(graphQLClient);
  const { mutateAsync: deleteCartItemById } = useDeleteCartItemByIdMutation(graphQLClient);
  const { refetch } = useGetCartQuery(
    graphQLClient,
    {
      input: {
        shouldReturnCalculation: false
      }
    },
    { enabled: false }
  );

  const [cart, setCart] = useRecoilState(cartState);
  const [selectedProduct, setSelectedProduct] = useRecoilState(priceModalSelectedProductState);
  const [isModalOpened, toggleModal] = useRecoilState(isPriceModalOpenedState);
  const setPreselectPrice = useSetRecoilState(priceModalSelectedPriceState);
  const setIsAdding = useSetRecoilState(isAddingCartState);
  const platform = useRecoilValue(platformState);
  const [isBuyNow, setIsBuyNow] = useRecoilState(isBuyNowState);
  const setModalCancelingItem = useSetRecoilState(modalCancelingItemState);
  const [suggestionItems, setSuggestionItems] = useRecoilState(suggestionItemsState);
  const setOrderCalculation = useSetRecoilState(orderCalculationState);
  const [isUpdating, setIsUpdating] = useRecoilState(isCartUpdatingState);
  const promoCode = useRecoilValue(promoCodeState);
  const setIsDetailModalOpened = useSetRecoilState(isDetailModalOpenedState);
  const setSameEvalModalOpened = useSetRecoilState(isSameEvalModalOpenedState);
  const setSameEvalId = useSetRecoilState(sameEvalIdState);
  const setIsConflictPlatformModalOpened = useSetRecoilState(isConfictPlatform);
  const activeProducts = useRecoilValue(activeProductsState);
  const pendingEvalIds = useRecoilValue(pendingEvalIdsState);
  const setIsRemoving = useSetRecoilState(isCancelModalRemovingState);
  const { excludedEvalIds } = useFetchExcludedEvalIds(initExcludeEvalIds);

  const completedEvalIds = activeProducts?.completedEvalIds ?? [];
  const skippedEvalIds = activeProducts?.skippedEvalIds ?? [];
  // react state needSelectConsultType
  const [needSelectConsultType, setNeedSelectConsultType] = useState(false);
  // set recoild state selectedConsultTypeState
  const setSelectedConsultType = useSetRecoilState(selectedConsultTypeState);

  const hasUnpaidTextConsult = useMemo(
    () =>
      cart?.cartItems?.some((cartItem) => {
        const isTextConsult = cartItem.consultationSessionType === ConsultationSessionType.Text;
        const hasPaidEvaluation =
          cart?.paidCompletedEvaluationIds?.includes(cartItem.product.evaluationId[platform]) ??
          false;

        return isTextConsult && !hasPaidEvaluation;
      }),
    [cart?.cartItems, cart?.paidCompletedEvaluationIds, platform]
  );

  const setShowPriceSelectionStep = useSetRecoilState(priceModalShowPriceStep);

  const onConfirmPriceClick = useCallback(
    async (priceId: string, consultType = ConsultationSessionType.Text) => {
      setIsAdding(true);

      const existedItem = find((item) => item.product.id === selectedProduct.id, cart.cartItems);
      let result: CartCalculationModel;

      if (!isNil(existedItem)) {
        const oldPriceId = existedItem.price.id;
        if (oldPriceId === priceId) {
          onClosePriceModal();
        } else {
          await updateCartItemById(
            {
              input: {
                sysId: cart.sysId,
                cartItem: {
                  sysId: existedItem.sysId,
                  productId: selectedProduct.id,
                  priceId
                },
                shouldReturnCalculation: true,
                ...(promoCode && { promotionCode: promoCode })
              }
            },
            {
              onSuccess: async (successData) => {
                result = successData?.updateCartItemById;
                setCart(result);
                onClosePriceModal();
                setOrderCalculation(result.calculation);
                queryClient.invalidateQueries(["getExcludedEvaluationIds"]);
              }
            }
          );
        }
      } else {
        await insertCartItemById(
          {
            input: {
              sysId: cart.sysId,
              productId: selectedProduct.id,
              selectedPriceId: priceId,
              shouldReturnCalculation: true,
              ...(promoCode && { promotionCode: promoCode }),
              fbc: localStorage.fbc,
              fbp: localStorage.fbp,
              completedEval: { evalId: selectedProduct?.evaluationId?.[platform] },
              utm: getUtm(),
              tracking: getTracking(),
              consultationSessionType: consultType
            }
          },
          {
            onSuccess: async (successData) => {
              result = successData?.insertCartItemById;
              if (result) {
                setCart(result);
                onClosePriceModal();
                queryClient.invalidateQueries(["getExcludedEvaluationIds"]);
                if (isBuyNow) {
                  await router.push("/checkout");
                }
                setOrderCalculation(result.calculation);
                updateSuggestionItems(selectedProduct.id);
              }
            }
          }
        );
      }

      setIsAdding(false);
    },
    [cart, selectedProduct, isBuyNow, promoCode, isSuggestion]
  );

  const updateSuggestionItems = useCallback(
    (productId: string) => {
      setSuggestionItems(suggestionItems?.filter((product) => product.id !== productId));
    },
    [suggestionItems]
  );

  const checkCart = async () => {
    if (isNil(cart.sysId)) {
      const { data: newCartData } = await refetch();

      setCart(newCartData.getCart);
      return newCartData.getCart.sysId;
    }
    return cart.sysId;
  };

  const onAddToCartClick = useCallback(
    async (product: any) => {
      // if product platform does not match patient.fromPlatformEnv then show error popup
      const validPlatforms = product.metadata?.platform.split(",") || [];
      if (!validPlatforms.includes(patient?.fromPlatformEnv)) {
        setIsConflictPlatformModalOpened({
          conflict: true,
          shouldPlatform: capitalize(platform)
        });
        return;
      }
      const evalId = product?.evaluationId?.[platform] ?? "";
      const orderGynaeHormoneTest = cart?.cartItems?.map(
        (currentCart) => currentCart.product.uniqName
      );
      if (!isUpdating) {
        if (checkOrderGynaeHormoneTest(product, orderGynaeHormoneTest)) {
          setSameEvalModalOpened(true);
          setSameEvalId(product.uniqName);
        } else if (excludedEvalIds.includes(evalId)) {
          setSameEvalModalOpened(true);
          setSameEvalId(evalId);
        } else if (product?.prices?.length === 1 && product.metadata.bundle !== "true") {
          onOTCHandler(product);
        } else {
          setSelectedProduct(product);
          toggleModal(true);
        }
      }
    },

    [cart, isUpdating, excludedEvalIds]
  );

  const onEditPriceClick = useCallback(
    async (cartItem: any) => {
      setSelectedProduct({
        ...cartItem.product,
        isSubscriptionPrice: cartItem.isSubscriptionPrice
      });
      setPreselectPrice({
        priceId: cartItem?.price?.id
      });

      toggleModal(true);
      if (cartItem.consultationSessionType) {
        setSelectedConsultType(cartItem.consultationSessionType);
        setShowPriceSelectionStep(true);
      } else {
        setSelectedConsultType(ConsultationSessionType.Text);
        setShowPriceSelectionStep(false);
      }
    },

    [cart]
  );

  const onBuyNowClick = useCallback(
    async (product: any): Promise<any> => {
      const evalId = product?.evaluationId?.[platform]?.[0] || "";

      const productName = product?.uniqName || convertStringToEnumKey(product?.name);

      if (
        [ProductNameType.GynaeConsult, ProductNameType.GynaeConsultHormoneTest].includes(
          productName
        )
      ) {
        if (GynaeGroupProducts.includes(productName)) {
          const orderGynaeHormoneTest = cart?.cartItems?.map((c) => c.uniqName);
          if (checkOrderGynaeHormoneTest(product, orderGynaeHormoneTest)) {
            setSameEvalModalOpened(true);
            setSameEvalId(productName);
            return;
          }
        }

        if (productName === ProductNameType.GynaeConsult) {
          return router.push("/prenatal/gyn");
        }

        return router.push(`/hormoneTest?productId=${product.id}`);
      }

      if (productName === ProductNameType.FertilityCoach) {
        setIsDetailModalOpened(false);

        await router.push("/prenatal/coach");
        return;
      }
      const orderGynaeHormoneTest = cart?.cartItems?.map(
        (currentCart) => currentCart.product.uniqName
      );
      if (checkOrderGynaeHormoneTest(product, orderGynaeHormoneTest)) {
        setSameEvalModalOpened(true);
        setSameEvalId(product.uniqName);

        return;
      }

      let requiresPrescription = product.metadata.requires_prescription;
      if (ENABLE_NEW_PRESCRIPTION_FLOW) {
        if (requiresPrescription) {
          const hasCompletedEval = completedEvalIds.includes(product?.evaluationId?.[platform]);

          return router.push(
            hasCompletedEval
              ? URL_PRESCRIPTION_FLOW_WITH_EVAL_ID_V2(evalId)
              : URL_PATIENT_EVAL_V2(evalId)
          );
        }
      } else {
        if (
          [ProductNameType.CovidTeleconsult, ProductNameType.SexTherapy].includes(product.uniqName)
        ) {
          // treat this product like otc product
          requiresPrescription = false;
        }

        if (requiresPrescription && !completedEvalIds.includes(product?.evaluationId?.[platform])) {
          toggleModal(false);
          await router.push(`/evaluation/${evalId}?productId=${product.id}`);

          return;
        }
      }

      if (
        (product?.prices?.length === 1 && product.metadata.bundle !== "true") ||
        product.metadata.HasEvaluation === "false"
      ) {
        onOTCHandler(product, true);

        return;
      }

      setIsBuyNow(true);
      onAddToCartClick(product);
    },

    [cart, pendingEvalIds, completedEvalIds, excludedEvalIds]
  );

  // already remove this feature. Someplace still use it. Will check and remove later
  const onThankYouBuyNowClick = useCallback(
    async (product: any) => {
      const newCartId = await checkCart();
      if (product?.prices?.length === 1) {
        await insertCartItemById(
          {
            input: {
              sysId: newCartId,
              productId: product.id,
              selectedPriceId: product?.prices?.[0]?.id,
              shouldReturnCalculation: false,
              fbc: localStorage.fbc,
              fbp: localStorage.fbp,
              utm: getUtm(),
              tracking: getTracking()
            }
          },
          {
            onSuccess: async (successData) => {
              const result = successData?.insertCartItemById;
              if (result) {
                setCart(result);
                await router.push("/checkout");
              }
            }
          }
        );
      } else {
        setIsBuyNow(true);
        toggleModal(true);
        setSelectedProduct(product);
      }
    },
    [cart, isSuggestion]
  );

  // already remove this feature. Someplace still use it. Will check and remove later
  const onThankYouAddToCartClick = useCallback(
    async (product: any) => {
      const newCartId = await checkCart();

      if (!isUpdating) {
        if (product?.prices?.length === 1) {
          await insertCartItemById(
            {
              input: {
                sysId: newCartId,
                productId: product.id,
                selectedPriceId: product.prices[0].id,
                shouldReturnCalculation: false,
                ...(promoCode && { promotionCode: promoCode }),
                fbc: localStorage.fbc,
                fbp: localStorage.fbp,
                utm: getUtm(),
                tracking: getTracking()
              }
            },
            {
              onSuccess: async (successData) => {
                const result = successData?.insertCartItemById;
                if (result) {
                  setCart(result);
                }
              }
            }
          );
        } else {
          toggleModal(true);
          setSelectedProduct(product);
        }
      }
    },
    [cart, isSuggestion]
  );

  const onOTCHandler = useCallback(
    async (product: any, redirect = false, autoRenewal = true) => {
      const evalId = product?.evaluationId?.[platform] ?? "";
      setIsUpdating(true);

      if (excludedEvalIds.includes(evalId)) {
        setSameEvalModalOpened(true);
        setSameEvalId(evalId);
        setIsUpdating(false);
        return;
      }
      await insertCartItemById(
        {
          input: {
            sysId: cart.sysId,
            productId: product.id,
            selectedPriceId: product.prices[0].id,
            shouldReturnCalculation: true,
            ...(promoCode && { promotionCode: promoCode }),
            fbc: localStorage.fbc,
            fbp: localStorage.fbp,
            utm: getUtm(),
            tracking: getTracking(),
            autoRenewal
          }
        },
        {
          onSuccess: async (successData) => {
            const result = successData?.insertCartItemById;
            if (result) {
              setCart(result);
              setOrderCalculation(result.calculation);
              updateSuggestionItems(product.id);

              if (redirect) {
                await router.push("/checkout");
              }
            }
          }
        }
      );

      setIsUpdating(false);
    },
    [cart, promoCode, isSuggestion, excludedEvalIds]
  );

  const onEvalAddToCartClick = useCallback(
    async ({
      productId,
      priceId,
      completedEval,
      teleConsultForSkipEval,
      isSkipMedSelecting,
      patientNote,
      autoRenewal
    }: AddToCartClickPayload) => {
      const data = await insertCartItemByIdAsync({
        input: {
          sysId: cart.sysId,
          productId,
          selectedPriceId: priceId,
          completedEval,
          teleConsultForSkipEval,
          shouldReturnCalculation: true,
          isSkipMedSelecting,
          fbc: localStorage.fbc,
          fbp: localStorage.fbp,
          patientNote,
          autoRenewal: isNil(autoRenewal) ? true : autoRenewal,
          utm: getUtm(),
          tracking: getTracking(),
          consultationSessionType: completedEval?.consultType || ConsultationSessionType.Video
        }
      });

      const result = data?.insertCartItemById;

      if (result) {
        setCart(result);
        setOrderCalculation(result.calculation);
      }
    },
    [cart, isSuggestion]
  );

  const onQuantityChange = useCallback(
    async ({ productId, cartItemId, quantity }) => {
      setIsUpdating(true);

      await updateCartItemById(
        {
          input: {
            sysId: cart.sysId,
            cartItem: {
              sysId: cartItemId,
              productId,
              quantity
            },
            shouldReturnCalculation: true,
            ...(promoCode && { promotionCode: promoCode })
          }
        },
        {
          onSuccess: async (successData) => {
            const result = successData?.updateCartItemById;

            if (result) {
              setCart(result);
              setOrderCalculation(result.calculation);
            }
          }
        }
      );

      setIsUpdating(false);
    },
    [cart, promoCode]
  );

  const onRemoveItemClick = useCallback(
    async (cartItem: any) => {
      setIsUpdating(true);
      setIsRemoving(true);

      await deleteCartItemById(
        {
          input: {
            cartItemId: cartItem.sysId,
            sysId: cart.sysId,
            shouldReturnCalculation: true,
            ...(promoCode && { promotionCode: promoCode })
          }
        },
        {
          onSuccess: async (successData) => {
            const result = successData.deleteCartItemById;

            if (result) {
              setCart(result);
              setOrderCalculation(result.calculation);
              setModalCancelingItem(null);
            }
          }
        }
      );

      setIsUpdating(false);
      setIsRemoving(false);
    },
    [cart, promoCode]
  );

  const onOpenCancelModal = useCallback((cartItem: any) => {
    setModalCancelingItem(cartItem);
  }, []);

  const onClosePriceModal = useCallback(() => {
    setIsBuyNow(false);
    toggleModal(false);
    setIsDetailModalOpened(false);
  }, []);

  const onTogglePriceClick = useCallback(
    async (cartItem) => {
      setIsUpdating(true);
      await updateCartItemById(
        {
          input: {
            sysId: cart.sysId,
            cartItem: {
              sysId: cartItem.sysId,
              productId: cartItem.product.id,
              isSubscriptionPrice: !cartItem.isSubscriptionPrice,
              isAutoRenew: !cartItem.isSubscriptionPrice
            },
            shouldReturnCalculation: true,
            ...(promoCode && { promotionCode: promoCode })
          }
        },
        {
          onSuccess: async (successData) => {
            const result = successData?.updateCartItemById;
            if (result) {
              setCart(result);
              setOrderCalculation(result.calculation);
            }
          }
        }
      );

      setIsUpdating(false);
    },
    [cart, promoCode]
  );

  const onToggleAutoRenew = useCallback(
    async (cartItem) => {
      setIsUpdating(true);
      await updateCartItemById(
        {
          input: {
            sysId: cart.sysId,
            cartItem: {
              sysId: cartItem.sysId,
              productId: cartItem.product.id,
              isSubscriptionPrice: cartItem.isSubscriptionPrice,
              isAutoRenewal: !cartItem.isAutoRenewal
            },
            shouldReturnCalculation: true,
            ...(promoCode && { promotionCode: promoCode })
          }
        },
        {
          onSuccess: async (successData) => {
            const result = successData?.updateCartItemById;
            if (result) {
              setCart(result);
              setOrderCalculation(result.calculation);
            }
          }
        }
      );

      setIsUpdating(false);
    },
    [cart, promoCode]
  );

  useEffect(() => {
    // ex: selectedProduct.evaluationId = { "noah": "ed" }
    const evaluationId = Object.values(selectedProduct?.evaluationId || {})[0] as EvalIds;
    const value =
      evaluationId &&
      [EvalIds.Ed, EvalIds.Pe, EvalIds.Hairloss].includes(evaluationId) &&
      checkCountry([RegionType.Hk, RegionType.Jp]);
    setNeedSelectConsultType(value);
  }, [selectedProduct]);

  return {
    onAddToCartClick,
    onQuantityChange,
    onRemoveItemClick,
    onConfirmPriceClick,
    onOpenCancelModal,
    onBuyNowClick,
    onEvalAddToCartClick,
    onClosePriceModal,
    onTogglePriceClick,
    onToggleAutoRenew,
    onEditPriceClick,
    isModalOpened,
    isUpdating,
    onThankYouBuyNowClick,
    onThankYouAddToCartClick,
    completedEvalIds,
    skippedEvalIds,
    platform,
    cart,
    promoCode,
    pendingEvalIds,
    onOTCHandler,
    setSameEvalModalOpened,
    setSameEvalId,
    setCart,
    needSelectConsultType,
    hasUnpaidTextConsult
  };
};

export default useCartHooks;
